import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { personalInfo } from '../data/mockData';
import { COLORS, SPACING } from '../constants/designSystem';
import { Mail, MapPin, Phone, Send } from 'lucide-react';
import { supabase } from "@/lib/supabase";

const ContactPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.subject) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      const { error } = await supabase.functions.invoke("president-raindas-website-contact-form", {
        body: formData,
      });
      if (error) throw error;
      setIsSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setErrors({ submit: 'Failed to send message. Please try again later.' });
      console.error('Error submitting contact form:', error);
    } finally {
      setIsSubmitting(false);
    }

    // Simulate API call (will be replaced with backend integration)
    // setTimeout(() => {
    //   console.log('Form submitted:', formData);
    //   setIsSubmitting(false);
    //   setIsSuccess(true);
    //   setFormData({ name: '', email: '', subject: '', message: '' });

    //   setTimeout(() => {
    //     setIsSuccess(false);
    //   }, 5000);
    // }, 2000);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const subjects = [
    { value: 'project', label: 'Project Inquiry' },
    { value: 'collaboration', label: 'Collaboration' },
    { value: 'mentorship', label: 'Mentorship Request' },
    { value: 'speaking', label: 'Speaking Engagement' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <div style={{ backgroundColor: COLORS.background, minHeight: '100vh', paddingTop: '80px' }}>
      {/* Hero */}
      <section
        style={{
          padding: `${SPACING.xxxl} ${SPACING.lg}`,
          textAlign: 'center',
          background: `radial-gradient(circle at 50% 50%, ${COLORS.primary}08 0%, transparent 60%)`,
        }}
      >
        <div
          style={{
            maxWidth: '800px',
            margin: '0 auto',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 600ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <div
            style={{
              display: 'inline-block',
              padding: `${SPACING.xs} ${SPACING.md}`,
              backgroundColor: `${COLORS.primary}15`,
              border: `1px solid ${COLORS.primary}40`,
              borderRadius: '50px',
              marginBottom: SPACING.md,
            }}
          >
            <span style={{ fontSize: '14px', color: COLORS.primary, fontWeight: '500' }}>Get In Touch</span>
          </div>
          <h1
            style={{
              fontSize: '56px',
              fontWeight: '700',
              color: COLORS.text.primary,
              marginBottom: SPACING.md,
            }}
          >
            Let's Connect
          </h1>
          <p
            style={{
              fontSize: '18px',
              lineHeight: '1.6',
              color: COLORS.text.secondary,
            }}
          >
            Have a project in mind, need mentorship, or just want to chat about tech? I'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section style={{ padding: `${SPACING.xl} ${SPACING.lg} ${SPACING.xxxl}` }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: SPACING.xxl,
            }}
            className="contact-grid"
          >
            {/* Contact Info */}
            <div
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 600ms cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <h2
                style={{
                  fontSize: '32px',
                  fontWeight: '700',
                  color: COLORS.text.primary,
                  marginBottom: SPACING.lg,
                }}
              >
                Contact Information
              </h2>
              <p
                style={{
                  fontSize: '16px',
                  lineHeight: '1.6',
                  color: COLORS.text.secondary,
                  marginBottom: SPACING.xl,
                }}
              >
                Feel free to reach out through any of these channels. I typically respond within 24-48 hours.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.lg }}>
                {[
                  { icon: Mail, label: 'Email', value: personalInfo.email, href: `mailto:${personalInfo.email}` },
                  { icon: Phone, label: 'Phone', value: personalInfo.phone, href: `tel:${personalInfo.phone}` },
                  { icon: MapPin, label: 'Location', value: personalInfo.location },
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={index}
                      style={{
                        padding: SPACING.md,
                        backgroundColor: COLORS.cardSurface,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: SPACING.md,
                        transition: 'all 300ms',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = COLORS.primary;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = COLORS.border;
                      }}
                    >
                      <div
                        style={{
                          width: '48px',
                          height: '48px',
                          backgroundColor: `${COLORS.primary}15`,
                          border: `1px solid ${COLORS.primary}40`,
                          borderRadius: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon size={22} color={COLORS.primary} />
                      </div>
                      <div>
                        <p style={{ fontSize: '13px', color: COLORS.text.muted, marginBottom: '4px' }}>{item.label}</p>
                        {item.href ? (
                          <a
                            href={item.href}
                            style={{
                              fontSize: '16px',
                              color: COLORS.text.primary,
                              textDecoration: 'none',
                              fontWeight: '500',
                            }}
                            onMouseEnter={(e) => (e.target.style.color = COLORS.primary)}
                            onMouseLeave={(e) => (e.target.style.color = COLORS.text.primary)}
                          >
                            {item.value}
                          </a>
                        ) : (
                          <p style={{ fontSize: '16px', color: COLORS.text.primary, fontWeight: '500' }}>
                            {item.value}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                style={{
                  marginTop: SPACING.xl,
                  padding: SPACING.lg,
                  backgroundColor: `${COLORS.primary}10`,
                  border: `1px solid ${COLORS.primary}30`,
                  borderRadius: '12px',
                }}
              >
                <p style={{ fontSize: '14px', color: COLORS.primary, fontWeight: '600', marginBottom: '4px' }}>
                  {personalInfo.status}
                </p>
                <p style={{ fontSize: '13px', color: COLORS.text.secondary }}>
                  Currently accepting new projects and collaboration opportunities
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                transition: 'all 600ms cubic-bezier(0.4, 0, 0.2, 1) 100ms',
              }}
            >
              <form
                onSubmit={handleSubmit}
                style={{
                  padding: SPACING.xl,
                  backgroundColor: COLORS.cardSurface,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '16px',
                }}
              >
                <h3
                  style={{
                    fontSize: '24px',
                    fontWeight: '600',
                    color: COLORS.text.primary,
                    marginBottom: SPACING.lg,
                  }}
                >
                  Send Me a Message
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.md }}>
                  {/* Name */}
                  <div>
                    <label
                      htmlFor="name"
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: COLORS.text.primary,
                        marginBottom: SPACING.xs,
                      }}
                    >
                      Your Name *
                    </label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="John Doe"
                      style={{
                        backgroundColor: COLORS.surface,
                        border: `1px solid ${errors.name ? '#ef4444' : COLORS.border}`,
                        color: COLORS.text.primary,
                        borderRadius: '8px',
                        padding: SPACING.sm,
                        fontSize: '15px',
                      }}
                    />
                    {errors.name && (
                      <span style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px', display: 'block' }}>
                        {errors.name}
                      </span>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label
                      htmlFor="email"
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: COLORS.text.primary,
                        marginBottom: SPACING.xs,
                      }}
                    >
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="john@example.com"
                      style={{
                        backgroundColor: COLORS.surface,
                        border: `1px solid ${errors.email ? '#ef4444' : COLORS.border}`,
                        color: COLORS.text.primary,
                        borderRadius: '8px',
                        padding: SPACING.sm,
                        fontSize: '15px',
                      }}
                    />
                    {errors.email && (
                      <span style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px', display: 'block' }}>
                        {errors.email}
                      </span>
                    )}
                  </div>

                  {/* Subject */}
                  <div>
                    <label
                      htmlFor="subject"
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: COLORS.text.primary,
                        marginBottom: SPACING.xs,
                      }}
                    >
                      Subject *
                    </label>
                    <Select value={formData.subject} onValueChange={(value) => handleChange('subject', value)}>
                      <SelectTrigger
                        style={{
                          backgroundColor: COLORS.surface,
                          border: `1px solid ${errors.subject ? '#ef4444' : COLORS.border}`,
                          color: COLORS.text.primary,
                          borderRadius: '8px',
                          padding: SPACING.sm,
                        }}
                      >
                        <SelectValue placeholder="Select a subject" />
                      </SelectTrigger>
                      <SelectContent>
                        {subjects.map((subj) => (
                          <SelectItem key={subj.value} value={subj.value}>
                            {subj.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.subject && (
                      <span style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px', display: 'block' }}>
                        {errors.subject}
                      </span>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label
                      htmlFor="message"
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '500',
                        color: COLORS.text.primary,
                        marginBottom: SPACING.xs,
                      }}
                    >
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      placeholder="Tell me about your project or inquiry..."
                      rows={6}
                      style={{
                        backgroundColor: COLORS.surface,
                        border: `1px solid ${errors.message ? '#ef4444' : COLORS.border}`,
                        color: COLORS.text.primary,
                        borderRadius: '8px',
                        padding: SPACING.sm,
                        fontSize: '15px',
                        resize: 'vertical',
                      }}
                    />
                    {errors.message && (
                      <span style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px', display: 'block' }}>
                        {errors.message}
                      </span>
                    )}
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting || isSuccess}
                    style={{
                      backgroundColor: isSuccess ? COLORS.primary : COLORS.primary,
                      color: COLORS.background,
                      padding: SPACING.md,
                      fontSize: '16px',
                      fontWeight: '600',
                      border: 'none',
                      borderRadius: '10px',
                      cursor: isSubmitting || isSuccess ? 'not-allowed' : 'pointer',
                      opacity: isSubmitting ? 0.7 : 1,
                      transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                      marginTop: SPACING.sm,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: SPACING.xs,
                      boxShadow: `0 0 30px ${COLORS.primary}40`,
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting && !isSuccess) {
                        e.currentTarget.style.backgroundColor = COLORS.primaryDark;
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = `0 8px 40px ${COLORS.primary}60`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubmitting && !isSuccess) {
                        e.currentTarget.style.backgroundColor = COLORS.primary;
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = `0 0 30px ${COLORS.primary}40`;
                      }
                    }}
                  >
                    {isSubmitting ? 'Sending...' : isSuccess ? 'Message Sent!' : 'Send Message'}
                    {!isSubmitting && !isSuccess && <Send size={18} />}
                  </Button>

                  {isSuccess && (
                    <div
                      style={{
                        padding: SPACING.md,
                        backgroundColor: `${COLORS.primary}15`,
                        border: `1px solid ${COLORS.primary}40`,
                        borderRadius: '8px',
                        fontSize: '14px',
                        color: COLORS.primary,
                        textAlign: 'center',
                      }}
                    >
                      Thank you for reaching out! I'll get back to you soon.
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 1024px) {
          .contact-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ContactPage;
